// Everything due, across every client — the "what does this week look like" view.
//
// Drilling into a client must not cost the cross-cutting views
// (planning/crum/client-surface.md §3), and this is the one that would otherwise be
// impossible: a date only means something next to every other date, and per-client
// pages can't show you that.
//
// Group milestones and item dates BOTH appear here, unlike on a client page where
// only milestones do. The distinction is the audience: this is the team's own
// planning surface, so the detail is the point.

import { Link } from 'react-router-dom'

import { EmptyState, Section } from '../components/common'
import { View } from '../components/Page'
import { Priority, VisibilityEye } from '../components/Priority'
import { dueIn, todayIso } from '../lib/format'
import { colorVar, dueTone, itemStatus } from '../lib/model'
import { blockersOf, fieldStr, type GroupRecord, type ItemRecord } from '../lib/reducer'
import { useStore } from '../lib/store'

type Row =
  | { kind: 'milestone'; due: string; rec: GroupRecord }
  | { kind: 'item'; due: string; rec: ItemRecord; board: string }

export function DuePage() {
  const { state, version } = useStore()
  void version

  const rows: Row[] = []
  for (const g of Object.values(state.groups)) {
    if (!g.archived && g.due) rows.push({ kind: 'milestone', due: g.due, rec: g })
  }
  for (const i of Object.values(state.items)) {
    if (i.archived || !i.due || i.status === 'done') continue
    const board = state.groups[i.group]?.work ?? ''
    rows.push({ kind: 'item', due: i.due, rec: i, board })
  }
  rows.sort((a, b) => a.due.localeCompare(b.due))

  const now = todayIso()
  const overdue = rows.filter((r) => r.due < now)
  const soon = rows.filter((r) => r.due >= now)

  if (rows.length === 0) {
    return (
      <View>
        <EmptyState
          icon="Check"
          title="Nothing has a date"
          hint="Give a group a date and it becomes a milestone, here and on the client's page."
        />
      </View>
    )
  }

  return (
    // The one list page that drew its own eyebrow, H1, rule and lead paragraph
    // INSIDE the frame, so it was the only tab in the strip that opened with a
    // title the tab had already said. The line it wanted is the view bar's hint.
    <View
      hint="Every milestone and every dated item, soonest first"
      actions={
        overdue.length > 0 ? (
          <span className="pill" style={{ '--pc': 'var(--crit)' } as React.CSSProperties}>
            <span className="tick" />
            {overdue.length} overdue
          </span>
        ) : undefined
      }
    >
      {overdue.length > 0 && (
        <Section title="Overdue" note={`${overdue.length}`}>
          <div className="rows">
            {overdue.map((r) => (
              <DueRow key={key(r)} row={r} state={state} />
            ))}
          </div>
        </Section>
      )}
      <Section title="Coming up" note={`${soon.length}`}>
        <div className="rows">
          {soon.map((r) => (
            <DueRow key={key(r)} row={r} state={state} />
          ))}
        </div>
      </Section>
    </View>
  )
}

function key(r: Row): string {
  return `${r.kind}:${r.rec.id}`
}

function DueRow({ row, state }: { row: Row; state: ReturnType<typeof useStore>['state'] }) {
  const boardId = row.kind === 'milestone' ? row.rec.work : row.board
  const board = state.entities[boardId]
  const blocked = row.kind === 'item' && blockersOf(state, row.rec.id).length > 0
  const status =
    row.kind !== 'item'
      ? null
      : blocked
        ? { label: 'Blocked', color: 'var(--crit)' }
        : {
            label: itemStatus(row.rec.status).label,
            color: `var(${colorVar(itemStatus(row.rec.status).color)})`,
          }

  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2.5">
      <span
        className="num text-body shrink-0 font-semibold"
        style={{ color: dueTone(row.due), minWidth: '6rem' }}
      >
        {row.due}
      </span>
      <span className="meta shrink-0" style={{ minWidth: '5.5rem' }}>
        {dueIn(row.due)}
      </span>

      <span className="min-w-0 flex-1">
        <span className="text-body" style={{ fontWeight: row.kind === 'milestone' ? 600 : 400 }}>
          {row.rec.label}
        </span>
        {row.kind === 'milestone' && <span className="meta ml-2">milestone</span>}
        {board && (
          <Link to={`/b/${boardId}`} className="meta ml-2 underline">
            {fieldStr(board, 'name')}
          </Link>
        )}
      </span>

      {status && (
        <span className="meta shrink-0" style={{ color: status.color }}>
          {status.label}
        </span>
      )}
      {row.kind === 'item' && (
        <span className="shrink-0" title="Their priority">
          <Priority value={row.rec.clientPriority} />
        </span>
      )}
      <span className="shrink-0">
        <VisibilityEye value={row.rec.visibility} />
      </span>
    </div>
  )
}
